import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data", "client"];
import { c as _c } from "react-compiler-runtime";
import { useEffect, useMemo } from 'react';
import { useAllHighlySensitivePropertyValues } from 'customer-data-properties/dataSensitivity/useAllHighlySensitivePropertyValues';
import { FOLDERS_POLLING_INTERVAL } from 'framework-listing-lib/internal/Folders/constants/folders';
import useFolderPollingState from 'framework-listing-lib/internal/Folders/hooks/useFolderPollingState';
import useIsMovingToAnotherFolder from 'framework-listing-lib/internal/Folders/hooks/useIsMovingToAnotherFolder';
import { useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { useViews as useViewsProvider } from 'framework-listing-lib/internal/ViewTabs/providers/ViewsProvider';
import useCrmObjectTypeFetchParams from 'framework-listing-lib/internal/hooks/useCrmObjectTypeFetchParams';
import useFetchFromCRM from 'framework-listing-lib/internal/hooks/useFetchFromCRM';
import useHandleDFCError from 'framework-listing-lib/internal/hooks/useHandleDFCError';
import useHasPulseCrmSearch from 'framework-listing-lib/internal/hooks/useHasPulseCrmSearch';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { setPropertyValues } from '../utils/crmObject';
import { useAppSettingsHaveLoaded } from '../internal/frameworkAppSettings/hooks/useFrameworkAppSettings';

/**
 * By default, we don't use fetchPolicy and hand everything over to data-fetching-client.
 * When in folders mode, DFC/GraphQL use "cache-and-network" so we always get the latest
 * results when entering a folder after moving objects in/out of a folder.
 * Pulse doesn't use fetchPolicy, so this only applies to DFC/GraphQL.
 */
function useFetchPolicy(fetchPolicy) {
  "use memo";

  const isFoldersMode = useIsFoldersMode();
  const hasPulseCrmSearch = useHasPulseCrmSearch();
  let t0;
  if (fetchPolicy) {
    t0 = fetchPolicy;
  } else {
    if (isFoldersMode && !hasPulseCrmSearch) {
      t0 = "cache-and-network";
    } else {
      t0 = undefined;
    }
  }
  return t0;
}
function usePollInterval() {
  "use memo";

  const isFoldersMode = useIsFoldersMode();
  const {
    pollingState
  } = useFolderPollingState();
  return isFoldersMode && pollingState === "ON" ? FOLDERS_POLLING_INTERVAL : undefined;
}

/**
 * Defer fetching of CRM objects if views are still loading.
 * This is to prevent making multiple API calls to fetch for CRM objects.
 */
export function useSkip() {
  "use memo";

  const {
    isLoading
  } = useViewsProvider();
  const appSettingsHaveLoaded = useAppSettingsHaveLoaded();
  const hasViews = useHasViews();
  return Boolean(hasViews && isLoading) || !appSettingsHaveLoaded;
}
export default function useFetchCrmObjectType(t0) {
  "use memo";

  const $ = _c(32);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {
      fetchPolicy: undefined
    } : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fetchingOptions = t1;
  const objectTypeId = useObjectTypeId();
  const fetchPolicy = useFetchPolicy(fetchingOptions.fetchPolicy);
  const pollInterval = usePollInterval();
  const skip = useSkip();
  const isFoldersMode = useIsFoldersMode();
  const {
    pollingState
  } = useFolderPollingState();
  const isMovingToAnotherFolder = useIsMovingToAnotherFolder();
  const variables = useCrmObjectTypeFetchParams();
  let t2;
  if ($[2] !== fetchPolicy || $[3] !== pollInterval || $[4] !== skip) {
    t2 = {
      pollInterval,
      skip,
      fetchPolicy
    };
    $[2] = fetchPolicy;
    $[3] = pollInterval;
    $[4] = skip;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = useFetchFromCRM(variables, t2);
  let data;
  let rest;
  if ($[6] !== t3) {
    const {
        data: t4
      } = t3,
      t5 = _objectWithoutPropertiesLoose(t3, _excluded);
    data = t4;
    rest = t5;
    $[6] = t3;
    $[7] = data;
    $[8] = rest;
  } else {
    data = $[7];
    rest = $[8];
  }
  const {
    stopPolling
  } = rest;
  useHandleDFCError(rest.error);
  let t4;
  let t5;
  if ($[9] !== isFoldersMode || $[10] !== pollingState || $[11] !== stopPolling) {
    t4 = () => {
      if (isFoldersMode && pollingState === "OFF") {
        stopPolling();
      }
    };
    t5 = [isFoldersMode, pollingState, stopPolling];
    $[9] = isFoldersMode;
    $[10] = pollingState;
    $[11] = stopPolling;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== objectTypeId) {
    t6 = {
      objectTypeId
    };
    $[14] = objectTypeId;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const highlySensitivePropertyValues = useAllHighlySensitivePropertyValues(t6);
  let t7;
  bb0: {
    if (!data) {
      t7 = data;
      break bb0;
    }
    let validObjects;
    if ($[16] !== data.results || $[17] !== highlySensitivePropertyValues || $[18] !== isFoldersMode || $[19] !== isMovingToAnotherFolder) {
      validObjects = data.results.filter(_temp);
      if (isFoldersMode) {
        let t8;
        if ($[21] !== isMovingToAnotherFolder) {
          t8 = crmObject_0 => !isMovingToAnotherFolder(crmObject_0.objectId);
          $[21] = isMovingToAnotherFolder;
          $[22] = t8;
        } else {
          t8 = $[22];
        }
        validObjects = validObjects.filter(t8);
      }
      const hasHighlySensitivePropertyValues = Object.keys(highlySensitivePropertyValues).length > 0;
      if (hasHighlySensitivePropertyValues) {
        let t8;
        if ($[23] !== highlySensitivePropertyValues) {
          t8 = crmObject_1 => highlySensitivePropertyValues[crmObject_1.objectId] ? setPropertyValues(crmObject_1, highlySensitivePropertyValues[crmObject_1.objectId]) : crmObject_1;
          $[23] = highlySensitivePropertyValues;
          $[24] = t8;
        } else {
          t8 = $[24];
        }
        validObjects = validObjects.map(t8);
      }
      $[16] = data.results;
      $[17] = highlySensitivePropertyValues;
      $[18] = isFoldersMode;
      $[19] = isMovingToAnotherFolder;
      $[20] = validObjects;
    } else {
      validObjects = $[20];
    }
    const invalidObjectsCount = data.results.length - validObjects.length;
    const t8 = validObjects;
    const t9 = Math.max(0, data.total - invalidObjectsCount);
    let t10;
    if ($[25] !== data || $[26] !== t9 || $[27] !== validObjects) {
      t10 = Object.assign({}, data, {
        results: t8,
        total: t9
      });
      $[25] = data;
      $[26] = t9;
      $[27] = validObjects;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    t7 = t10;
  }
  const validCrmObjects = t7;
  let t8;
  if ($[29] !== rest || $[30] !== validCrmObjects) {
    t8 = Object.assign({
      data: validCrmObjects
    }, rest);
    $[29] = rest;
    $[30] = validCrmObjects;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const value = t8;
  return value;
}
function _temp(crmObject) {
  return Boolean(crmObject.properties && Object.keys(crmObject.properties).length > 0);
}